import React from 'react';
import { Book, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ColdEmailDocs: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/email')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cold Email Tool
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <Book className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Cold Email Documentation</h1>
          </div>
          <p className="text-gray-600">Complete guide to using the Explanatum Cold Email Tool</p>
        </div>

        {/* Documentation Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 prose prose-blue max-w-none">
          <div className="mb-8">
            <p className="text-sm text-gray-500">Version 2.0 • Last Updated: October 10, 2025</p>
          </div>

          {/* Quick Start */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🚀 Quick Start Guide</h2>
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
              <p className="font-medium text-blue-900">New to cold email? Follow these steps to send your first campaign in 10 minutes.</p>
            </div>
            
            <ol className="space-y-4">
              <li className="pl-2">
                <strong>Configure a Mailbox</strong>
                <p className="text-gray-600 mt-1">Go to Mailboxes → Add Mailbox → Enter your SMTP/IMAP settings → Test Connection → Save</p>
              </li>
              <li className="pl-2">
                <strong>Import Contacts</strong>
                <p className="text-gray-600 mt-1">Go to Contacts → Import → Upload CSV with email, firstName, lastName, company columns</p>
              </li>
              <li className="pl-2">
                <strong>Create Campaign</strong>
                <p className="text-gray-600 mt-1">Go to Campaigns → New Campaign → Follow 5-step wizard → Use Template Library or AI Generator</p>
              </li>
              <li className="pl-2">
                <strong>Launch & Monitor</strong>
                <p className="text-gray-600 mt-1">Click Play button → Monitor Analytics dashboard → View Leads for hot prospects</p>
              </li>
            </ol>
          </section>

          {/* Key Features */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">✨ Key Features</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">📄 Template Library</h3>
                <p className="text-sm text-gray-600">15 pre-built templates for SaaS, Agency, Recruiting, eCommerce, and Real Estate with avg spam score of 13.5</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">✨ AI Email Generator</h3>
                <p className="text-sm text-gray-600">Generate professional email copy with Claude 3.7 Sonnet. Get 3 variations with spam score analysis</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">⚡ Personalization Assistant</h3>
                <p className="text-sm text-gray-600">Enrich emails with LinkedIn + company data. Scores personalization level 0-100</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">🕐 Send Time Optimizer</h3>
                <p className="text-sm text-gray-600">AI-powered send time recommendations based on your data. 15-25% higher open rates</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">🎯 Lead Intelligence</h3>
                <p className="text-sm text-gray-600">Auto-score leads 0-100. Categorize as Hot/Warm/Cold. Track engagement and sentiment</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">📊 Advanced Analytics</h3>
                <p className="text-sm text-gray-600">Track opens, replies, bounces. Compare campaigns. A/B test. Export data to CSV</p>
              </div>
            </div>
          </section>

          {/* Content Creation */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">✍️ Content Creation Options</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Option 1: Template Library</h3>
            <p className="text-gray-600 mb-4">Best for: Getting started quickly with proven templates</p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="font-mono text-sm text-gray-800">Campaigns → New → Step 2 → Click "Template Library" card → Browse → Select → Use</p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Option 2: AI Email Generator</h3>
            <p className="text-gray-600 mb-4">Best for: Custom emails generated from your requirements</p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="font-mono text-sm text-gray-800">Campaigns → New → Step 2 → Click "AI Generator" card → Fill form → Generate → Review 3 variations → Select best → Use</p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Option 3: Personalization Assistant</h3>
            <p className="text-gray-600 mb-4">Best for: Highly targeted outreach with prospect-specific data</p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="font-mono text-sm text-gray-800">Campaigns → New → Step 2 → Click "Personalization" card → Enter LinkedIn URL + company data → Generate → Review score (aim for 70+) → Use</p>
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">💡 Best Practices</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Subject Lines</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Keep under 50 characters</li>
                  <li>Avoid spam words (Free, Act Now, Limited Time)</li>
                  <li>Use personalization like {`{{firstName}}`}</li>
                  <li>Ask questions or create curiosity</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Email Body</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Keep under 150 words</li>
                  <li>One clear call-to-action</li>
                  <li>Short paragraphs (2-3 lines max)</li>
                  <li>Include personalization and social proof</li>
                  <li>Provide value first, ask second</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Timing</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Best days: Tuesday, Wednesday, Thursday</li>
                  <li>Best times: 9-11 AM, 2-4 PM (recipient's timezone)</li>
                  <li>Use Send Time Optimizer for data-driven recommendations</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Volume</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Start with 20-50 emails/day per mailbox</li>
                  <li>Increase gradually (10-20/day per week)</li>
                  <li>Never exceed 200 emails/day per mailbox</li>
                  <li>Warm up new mailboxes for 2-4 weeks</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Lead Intelligence */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🎯 Understanding Lead Intelligence</h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">Leads are automatically scored 0-100 based on engagement:</p>
              
              <div className="border-l-4 border-red-500 bg-red-50 p-4">
                <h3 className="font-semibold text-red-900 mb-2">🔥 Hot Leads (80-100)</h3>
                <p className="text-sm text-red-800 mb-2">High engagement + fast response + positive sentiment</p>
                <p className="text-sm text-red-900 font-medium">Action: Follow up within 24 hours. Expected conversion: 40-60%</p>
              </div>

              <div className="border-l-4 border-orange-500 bg-orange-50 p-4">
                <h3 className="font-semibold text-orange-900 mb-2">🌤️ Warm Leads (50-79)</h3>
                <p className="text-sm text-orange-800 mb-2">Moderate engagement + some interest shown</p>
                <p className="text-sm text-orange-900 font-medium">Action: Add to nurture sequence. Expected conversion: 15-25%</p>
              </div>

              <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
                <h3 className="font-semibold text-blue-900 mb-2">❄️ Cold Leads (0-49)</h3>
                <p className="text-sm text-blue-800 mb-2">Low/no engagement or negative response</p>
                <p className="text-sm text-blue-900 font-medium">Action: Re-engagement campaign after 2-3 weeks. Expected conversion: 5-10%</p>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🔧 Common Issues & Solutions</h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Low Open Rates (&lt;15%)</h3>
                <p className="text-sm text-gray-600 mb-2"><strong>Solutions:</strong></p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Test subject lines with A/B testing</li>
                  <li>Check deliverability in Analytics dashboard</li>
                  <li>Use Send Time Optimizer</li>
                  <li>Warm up mailbox if new</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Emails Going to Spam</h3>
                <p className="text-sm text-gray-600 mb-2"><strong>Solutions:</strong></p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Complete email warmup (2-4 weeks minimum)</li>
                  <li>Check spam score in AI Generator (aim for &lt;20)</li>
                  <li>Limit to 1-2 links maximum per email</li>
                  <li>Reduce sending speed (start with 20-50/day)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">High Bounce Rate (&gt;5%)</h3>
                <p className="text-sm text-gray-600 mb-2"><strong>Solutions:</strong></p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Use Email Validation before importing contacts</li>
                  <li>Remove hard bounces immediately</li>
                  <li>Verify email addresses at source</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Support */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">📞 Need More Help?</h2>
            <p className="text-gray-600 mb-4">
              For detailed documentation including API reference, advanced features, and troubleshooting:
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Full Documentation:</strong> Available in project files at <code className="bg-blue-100 px-2 py-1 rounded">COLD_EMAIL_DOCUMENTATION.md</code>
              </p>
              <p className="text-sm text-blue-900 mt-2">
                <strong>Support:</strong> Contact support@explanatum.com for assistance
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ColdEmailDocs;
